import * as React from 'react';
import propz from 'propz';
import { Component } from 'react';
import { AppUser } from 'Src/views/App/App';
import { Participant, SchoolEvent, SchoolEventIndividualData, SchoolEventTeamDataPlayer } from 'Src/models/event';
import { DEFAULT_LIMIT, DEFAULT_SKIP } from 'Src/consts/table';
import {
  getAllSchoolIdsFromEvent,
  getEventParticipants,
  getParticipantImg,
  getParticipantName,
  getParticipantText,
  getHouseColors
} from 'Src/helpers/event/event';
import { Loader } from 'Src/components/Loader/Loader';
import { ChildrenEventTeamPlayers } from './TeamPlayers/TeamPlayers';
import { ChildrenEventIndividualPlayers } from './IndividualPlayers/IndividualPlayers';
import './TeamsTab.scss';
import { getSchoolsPublic } from '../../../../../../helpers/service/public/public';
import { isEventStatusFinished, isMultipartyEvent } from '../../../../../../helpers/event/event';
import { SchoolEventCricketResult } from 'Src/views/GenericView/AdminView/TeamsAndEvents/Events/SchoolEventView/SchoolEventCricketResult';
import { isCricket } from '../../../../../../helpers/sport/cricket';
import { getChildrenEventKits } from 'Src/helpers/service/parent/childrenEvents';
import { KitShirtPreview } from 'Src/components/KitLab/KitShirtPreview';
import { KitShortsPreview } from 'Src/components/KitLab/KitShortsPreview';
import { KitSkortPreview } from 'Src/components/KitLab/KitSkortPreview';
import { KitLabBackendKit } from 'Src/helpers/service/admin/kitLab';
import { KitPatternVariant, KitBottomPattern, KitSkortPattern, ShortsVariant } from 'Src/types/kitLab';
import { isSkortPatternId, mapBottomToSkortPattern } from 'Src/helpers/kitLab/kitLab';
import { Child } from 'Src/models/child';
import { ParticipantResultBadge } from 'Src/components/ParticipantResultBadge/ParticipantResultBadge';
import { Report } from 'Src/models/report';
import { AvailabilityStatusLegend } from 'Src/components/AvailabilityStatusDot/AvailabilityStatusDot';

interface State {
  isLoading: boolean;
  participants: Participant[];
  eventDetailsBySchool: { [schoolId: string]: any };
  allKits: KitLabBackendKit[];
}

interface Props {
  user: AppUser;
  eventInitial: SchoolEvent;
  childrenInEvent: Child[];
  availabilityReports: Report[];
}

export class ChildrenEventTeamsSummaryTab extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      participants: [],
      eventDetailsBySchool: {},
      allKits: []
    };
  }

  async componentDidMount() {
    const { eventInitial, user } = this.props;
    const allSchoolIds = getAllSchoolIdsFromEvent(eventInitial);

    this.setState({
      isLoading: true
    });

    const filter = {
      where: { id: { $in: allSchoolIds } },
      view: {
        type: 'id_name_pic'
      },
      skip: DEFAULT_SKIP,
      limit: DEFAULT_LIMIT
    };

    try {
      const [schools, kitsResponse] = await Promise.all([
        getSchoolsPublic(filter),
        getChildrenEventKits(user, eventInitial.id)
      ]);

      const participants = getEventParticipants(eventInitial, user, schools);

      const eventDetailsBySchool: { [schoolId: string]: any } = {};
      const allKits: KitLabBackendKit[] = [];

      if (kitsResponse) {
        const mappings: any[] = (kitsResponse.mappings || []) as any[];
        const kits: KitLabBackendKit[] = (kitsResponse.kits || []) as KitLabBackendKit[];

        mappings.forEach(m => {
          if (!m) return;
          const sid = propz.get(m, ['schoolId'], null);
          const key = sid ? sid.toString() : 'default';
          eventDetailsBySchool[key] = m;
        });

        allKits.push(...kits);
      }

      this.setState({
        isLoading: false,
        participants,
        eventDetailsBySchool,
        allKits
      });
    } catch (e) {
      console.error('Failed to load children event teams/kits', e);
      this.setState({ isLoading: false });
    }
  }

  private getKitPreviewLayout(
    kit: KitLabBackendKit
  ): {
    shirtPattern: KitPatternVariant;
    primaryColor: string;
    secondaryColor: string;
    bottomIsSkort: boolean;
    shortsVariant: ShortsVariant;
    skortPattern: KitSkortPattern;
    shortsPrimaryColor: string;
    shortsSecondaryColor: string;
  } {
    let primaryColor = '#1f78ff';
    let secondaryColor = '#ffd24d';
    let shortsPrimaryColor = primaryColor;
    let shortsSecondaryColor = secondaryColor;
    let shirtPattern: KitPatternVariant = 'plain';
    let bottomPattern: KitBottomPattern = 'SHORTS_PLAIN';

    if (kit.layoutPreset) {
      try {
        const parsed = JSON.parse(kit.layoutPreset as any) as any;
        if (parsed) {
          if (parsed.primaryColor) primaryColor = parsed.primaryColor;
          if (parsed.secondaryColor) secondaryColor = parsed.secondaryColor;
          if (parsed.shortsPrimaryColor) shortsPrimaryColor = parsed.shortsPrimaryColor;
          if (parsed.shortsSecondaryColor) shortsSecondaryColor = parsed.shortsSecondaryColor;
          if (parsed.shirtPattern) shirtPattern = parsed.shirtPattern as KitPatternVariant;
          if (parsed.bottomPattern) bottomPattern = parsed.bottomPattern as KitBottomPattern;
        }
      } catch (e) {}
    }

    if (!kit.layoutPreset && Array.isArray(kit.segments)) {
      const primarySeg = kit.segments.find((s: any) => s.segmentKey === 'SHIRT_PRIMARY');
      const secondarySeg = kit.segments.find((s: any) => s.segmentKey === 'SHIRT_SECONDARY');
      const shortsPrimarySeg = kit.segments.find((s: any) => s.segmentKey === 'SHORTS_PRIMARY');
      const shortsSecondarySeg = kit.segments.find((s: any) => s.segmentKey === 'SHORTS_SECONDARY');

      if (primarySeg?.color) primaryColor = primarySeg.color;
      if (secondarySeg?.color) secondaryColor = secondarySeg.color;
      if (shortsPrimarySeg?.color) shortsPrimaryColor = shortsPrimarySeg.color;
      if (shortsSecondarySeg?.color) shortsSecondaryColor = shortsSecondarySeg.color;
    }

    const bottomIsSkort = isSkortPatternId(bottomPattern);
    const skortPattern = bottomIsSkort ? mapBottomToSkortPattern(bottomPattern) : 'plain';

    let shortsVariant: ShortsVariant = 'plain';
    if (!bottomIsSkort) {
      switch (bottomPattern) {
        case 'SHORTS_SIDE':
          shortsVariant = 'sideStripes';
          break;
        case 'SHORTS_TRIANGLES':
          shortsVariant = 'sideTriangles';
          break;
        case 'SHORTS_BIG_TRIANGLES':
          shortsVariant = 'sideBigTriangles';
          break;
        case 'SHORTS_SEMICIRCLES':
          shortsVariant = 'sideSemiCircles';
          break;
        default:
          shortsVariant = 'plain';
      }
    }

    return {
      shirtPattern,
      primaryColor,
      secondaryColor,
      bottomIsSkort,
      shortsVariant,
      skortPattern,
      shortsPrimaryColor,
      shortsSecondaryColor
    };
  }

  private getKitForParticipant(participant: Participant): KitLabBackendKit | null {
    const { eventDetailsBySchool, allKits } = this.state;

    if (!eventDetailsBySchool || !allKits || allKits.length === 0) return null;

    const participantSchoolId = propz.get(participant, ['school', 'id'], null);
    const schoolKey = participantSchoolId ? participantSchoolId.toString() : 'default';

    const detailsForSchool = eventDetailsBySchool[schoolKey] || eventDetailsBySchool['default'];
    if (!detailsForSchool) return null;

    const mappings: any[] = propz.get(detailsForSchool, ['kitLabKits'], []) || [];
    if (!mappings.length) return null;

    const teamId = propz.get(participant, ['team', 'id'], null);
    const teamIdStr = teamId ? teamId.toString() : null;

    let mapping: any = null;

    if (teamIdStr) {
      mapping =
        mappings.find(m => {
          const mTeamId = m.teamId ? m.teamId.toString() : null;
          return mTeamId === teamIdStr;
        }) || null;
    }

    if (!mapping) {
      mapping =
        mappings.find(m => {
          const hasTeam = !!m.teamId;
          return !hasTeam;
        }) || null;
    }

    if (!mapping || !mapping.kitId) return null;

    const kitIdStr = mapping.kitId.toString();
    const kit = allKits.find(k => k._id && k._id.toString() === kitIdStr) || null;

    return kit;
  }

  render() {
    const { eventInitial, user } = this.props;
    const { isLoading, participants } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const isCricketResultShow =
      isEventStatusFinished(eventInitial) && isCricket(eventInitial) && !isMultipartyEvent(eventInitial);

    return (
      <div className="mt-3">
        {isCricketResultShow && <SchoolEventCricketResult event={eventInitial} user={user} />}
        {this.props.childrenInEvent.length > 0 && <AvailabilityStatusLegend reports={this.props.availabilityReports} permissionIds={this.props.childrenInEvent.map(child => String(child.permissionId || child.id))} />}
        <div className="eSchoolParticipantScroll">
          {participants.map((participant, index) => {
            const participantName = getParticipantName(eventInitial, participant);
            const participantText = getParticipantText(eventInitial, participant, user);
            const participantImg = getParticipantImg(participant);
            const teamPlayers = propz.get<SchoolEventTeamDataPlayer[]>(participant, ['teamPlayers'], []);
            const individualPlayers = propz.get<SchoolEventIndividualData[]>(participant, ['individualPlayers'], []);

            const houseColors = getHouseColors(participant);

            const kit = this.getKitForParticipant(participant);
            const kitPreview = kit
              ? (() => {
                  const {
                    shirtPattern,
                    primaryColor,
                    secondaryColor,
                    bottomIsSkort,
                    shortsVariant,
                    skortPattern,
                    shortsPrimaryColor,
                    shortsSecondaryColor
                  } = this.getKitPreviewLayout(kit);

                  return (
                    <div className="eEventTeams__kitIcon">
                      <div className="eEventTeams__kitIcon-shirt">
                        <KitShirtPreview
                          variant={shirtPattern}
                          primaryColor={primaryColor}
                          secondaryColor={secondaryColor}
                          className="eKitLab__shirtSvg--mini"
                        />
                      </div>

                      <div className="eEventTeams__kitIcon-bottom">
                        {bottomIsSkort ? (
                          <KitSkortPreview
                            pattern={skortPattern}
                            primaryColor={shortsPrimaryColor}
                            secondaryColor={shortsSecondaryColor}
                            className="eKitLab__bottomSvg--mini eKitLab__bottomSvg--miniSkort"
                          />
                        ) : (
                          <KitShortsPreview
                            variant={shortsVariant}
                            primaryColor={shortsPrimaryColor}
                            secondaryColor={shortsSecondaryColor}
                            className="eKitLab__bottomSvg--mini"
                          />
                        )}
                      </div>
                    </div>
                  );
                })()
              : null;

            return (
              <div key={`participant_${index}`} className={'eParticipant'}>
                <div className="bSchoolLogoSections">
                  <img className="img-thumbnail eParticipantSchoolLogo" src={participantImg} />

                  <div className="font-weight-bold eParticipantNameRow">
                    <div className="eParticipantNameAndHouse">
                      <span className="eParticipantName">{participantName}</span>
                      {houseColors.length > 0 && (
                        <span className="house-colors">
                          {houseColors.map((color, index) => (
                            <span
                              key={index}
                              style={{
                                display: 'inline-block',
                                width: '10px',
                                height: '10px',
                                backgroundColor: color,
                                borderRadius: '50%',
                                marginLeft: '5px'
                              }}
                            />
                          ))}
                        </span>
                      )}
                    </div>

                    {kitPreview}
                  </div>
                  <div className="ePlayerScore">
                    <ParticipantResultBadge event={eventInitial} participant={participant} />
                  </div>
                </div>
                <div className="bSchoolPlayersSections">
                  {participantText ? (
                    <div className="eParticipantText">{participantText}</div>
                  ) : (
                    <>
                      <ChildrenEventTeamPlayers
                        players={teamPlayers}
                        event={eventInitial}
                        ownedChildren={this.props.childrenInEvent}
                        availabilityReports={this.props.availabilityReports}
                      />
                      <ChildrenEventIndividualPlayers
                        players={individualPlayers}
                        event={eventInitial}
                        ownedChildren={this.props.childrenInEvent}
                        availabilityReports={this.props.availabilityReports}
                      />
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}
